import type { Policy } from '@/lib/heyapi/types.gen';
import { TransactionStatus, TransactionType } from '@/lib/heyapi/types.gen';

/**
 * Returns `true` when a policy is a valid parent for a new renewal.
 *
 * A policy qualifies iff its latest transaction is `ACTIVE` and not a
 * cancellation. `details.policyType` is intentionally exposed in the
 * `Pick` but NOT consulted — a bound renewal is itself a valid parent
 * for the next renewal (DC-3151). The wider signature lets the
 * regression test in `__tests__/utils.test.tsx` pass a fixture with
 * `policyType: RENEWAL` and assert `true`, so reintroducing a
 * `policy.details.policyType !== RENEWAL` clause inside the helper
 * would surface as a failing test rather than a silent change.
 *
 * @param policy - Policy under consideration, or `null`/`undefined`.
 * @returns `true` when the policy is eligible to be renewed; `false`
 *   for null/undefined input or when the latest transaction is not
 *   `ACTIVE` or is a cancellation.
 */
export const isPolicyValidParentForRenewal = (
  policy: Pick<Policy, 'latestTransaction' | 'details'> | null | undefined
): boolean => {
  if (!policy) return false;
  return (
    policy.latestTransaction.status === TransactionStatus.ACTIVE &&
    policy.latestTransaction.transactionType !==
      TransactionType.POLICY_CANCELLED
  );
};

type BackendRateError = {
  path: string[];
  attribute: string;
  code: string;
  message: string;
};

type PydanticValidationError = {
  type?: string;
  loc?: (string | number)[];
  msg?: string;
};

const PYDANTIC_LOC_ROOTS = new Set([
  'body',
  'query',
  'path',
  'header',
  'cookie',
]);

const isValidPydanticError = (
  item: unknown
): item is PydanticValidationError => {
  return (
    item !== null &&
    typeof item === 'object' &&
    typeof (item as PydanticValidationError).msg === 'string'
  );
};

// Detects a Pydantic-shaped detail array. Permissive on purpose: as long as
// at least one item is parseable, we render the valid ones and skip the rest
// (a single malformed entry shouldn't hide the others under a generic fallback).
const isPydanticErrorList = (
  value: unknown
): value is PydanticValidationError[] => {
  return (
    Array.isArray(value) && value.length > 0 && value.some(isValidPydanticError)
  );
};

const pydanticFieldLabel = (loc: PydanticValidationError['loc']): string => {
  if (!Array.isArray(loc) || loc.length === 0) return '';
  const skip = PYDANTIC_LOC_ROOTS.has(String(loc[0])) ? 1 : 0;
  return loc.slice(skip).reduce<string>((acc, segment) => {
    if (typeof segment === 'number') return `${acc}[${segment}]`;
    return acc ? `${acc}.${segment}` : String(segment);
  }, '');
};

export const TEXT_PLACEHOLDER = '--';

function formattedPathAndMessage(error: BackendRateError): [string, string] {
  const entityPath = error.path.slice(0, -1).join(' -> '); // Exclude the last part (attribute)
  let { message } = error;

  if (error.attribute !== '') {
    message = `'${error.attribute}' is required`;
  }

  return [entityPath, message];
}

export const getCleanText = (input: string | unknown) => {
  if (typeof input === 'string' && input.trim()) {
    return input;
  }
  return TEXT_PLACEHOLDER;
};

const GENERIC_ERROR_MESSAGE =
  'An unexpected error occurred. Please try again or contact support.';

const renderGenericError = (message: string) => (
  <div className="rounded-md bg-red-400 p-4">
    <p className="whitespace-pre-wrap break-words font-medium text-white">
      {message}
    </p>
  </div>
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- accepts multiple backend error shapes, needs ApiError type (future ticket)
export const formatErrorMessage = (error: any, fallbackMessage?: string) => {
  const fallback = fallbackMessage || GENERIC_ERROR_MESSAGE;

  try {
    // Handle null/undefined error
    if (!error) {
      return renderGenericError(fallback);
    }

    // Handle string error
    if (typeof error === 'string') {
      return renderGenericError(error || fallback);
    }

    // Handle errors array format
    if (error.errors && Array.isArray(error.errors)) {
      return (
        <div className="max-h-[60vh] space-y-2 overflow-y-auto">
          {error.errors.map((err: BackendRateError, idx: number) => {
            const [entityPath, message] = formattedPathAndMessage(err);
            return (
              <div key={idx} className="rounded-md bg-red-400 p-3">
                <p className="break-words font-medium text-white">
                  {entityPath}
                </p>
                <p className="whitespace-pre-wrap break-words text-white/90">
                  {message}
                </p>
              </div>
            );
          })}
        </div>
      );
    }

    const errorDetail = error.detail;

    // Handle Pydantic 422 validation: detail = [{ type, loc, msg }, ...]
    // Filter out any malformed items so a single bad entry doesn't hide the rest.
    if (isPydanticErrorList(errorDetail)) {
      const validErrors = errorDetail.filter(isValidPydanticError);
      return (
        <div className="max-h-[60vh] space-y-2 overflow-y-auto">
          {validErrors.map((err, idx) => {
            const fieldLabel = pydanticFieldLabel(err.loc);
            return (
              <div key={idx} className="rounded-md bg-red-400 p-3">
                {fieldLabel ? (
                  <p className="break-words font-medium text-white">
                    {fieldLabel}
                  </p>
                ) : null}
                <p className="whitespace-pre-wrap break-words text-white/90">
                  {err.msg}
                </p>
              </div>
            );
          })}
        </div>
      );
    }

    // Handle error.detail as an object (e.g., wrapped { detail: { ... } }):
    // unwrap a known string field, otherwise fall back so React never renders the object.
    if (errorDetail && typeof errorDetail === 'object') {
      const nested = errorDetail as Record<string, unknown>;
      if (typeof nested.detail === 'string' && nested.detail.trim()) {
        return renderGenericError(nested.detail);
      }
      if (typeof nested.message === 'string' && nested.message.trim()) {
        return renderGenericError(nested.message);
      }
      return renderGenericError(fallback);
    }

    // Handle error.detail with Error() pattern - check errorDetail exists first
    if (
      typeof errorDetail === 'string' &&
      !errorDetail.includes('messages=[') &&
      errorDetail.includes('Error(')
    ) {
      const errorsStr = errorDetail.split(':')[1]?.trim();
      if (errorsStr) {
        const errors = errorsStr
          .slice(1, -1)
          .split('), Error(')
          .map((err: string) => {
            const messageMatch = err.match(/message='([^']+)'/);
            return {
              message: messageMatch ? messageMatch[1] : '',
            };
          })
          .filter((err: { message: string }) => err.message); // Filter out empty messages

        if (errors.length > 0) {
          return (
            <div className="max-h-[60vh] space-y-2 overflow-y-auto">
              {errors.map((err: { message: string }, idx: number) => (
                <div key={idx} className="rounded-md bg-red-400 p-3">
                  <p className="whitespace-pre-wrap break-words font-medium text-white">
                    {err.message}
                  </p>
                </div>
              ))}
            </div>
          );
        }
      }
    }

    // Handle error.detail as a plain string
    if (typeof errorDetail === 'string' && errorDetail.trim()) {
      return (
        <div className="rounded-md bg-red-400 p-4">
          <pre className="max-h-[60vh] overflow-y-auto whitespace-pre-wrap break-words font-medium text-white">
            {errorDetail}
          </pre>
        </div>
      );
    }

    // Handle error.message property (common error format)
    if (typeof error.message === 'string' && error.message.trim()) {
      return renderGenericError(error.message);
    }

    // Handle error.status_message property
    if (
      typeof error.status_message === 'string' &&
      error.status_message.trim()
    ) {
      return renderGenericError(error.status_message);
    }

    // Fallback to caller-supplied (or generic) error message
    return renderGenericError(fallback);
  } catch {
    // Catch-all: ensure we always return a meaningful error message
    return renderGenericError(fallback);
  }
};
